/**
 * Bottom Temperature data layer - checkbox + legend. Fetches temperature data when layer is on.
 * Uses useDataLayer for MapController; applies layout defaults so layer shows when provided in layout.
 */
import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import type { RegistryComponentProps } from '../../../pages/registry/componentRegistry';
import { useDataLayer } from '../hooks/useDataLayer';
import { useApi } from '../../../api/api';

const DEFAULT_DAYS_BACK = 90;

interface DateRange {
  startDate: string;
  endDate: string;
}

function getDateRange(daysBack: number = DEFAULT_DAYS_BACK): DateRange {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - (daysBack || DEFAULT_DAYS_BACK));
  const format = (d: Date): string => d.toISOString().slice(0, 10);
  return { startDate: format(start), endDate: format(end) };
}

const TEMP_LEGEND = [
  { color: '#0000FF', label: '< 0°C' },
  { color: '#4169E1', label: '0-5°C' },
  { color: '#00BFFF', label: '5-10°C' },
  { color: '#00FF7F', label: '10-15°C' },
  { color: '#FFFF00', label: '15-20°C' },
  { color: '#FFA500', label: '20-25°C' },
  { color: '#FF0000', label: '> 25°C' },
] as const;

export default function BottomTemperatureDataLayer(
  props: RegistryComponentProps
): React.ReactElement {
  const { slug, isReady, isActive, setActive, mapController } = useDataLayer(props);
  const { getBottomTemperature } = useApi();
  const [loading, setLoading] = useState(false);
  const syncedFromLayoutRef = useRef(false);

  // When layer is in layout, mapController has it active but our isActive may not have synced yet.
  // Proactively sync so we fetch and the Cesium layer shows initially.
  useLayoutEffect(() => {
    if (syncedFromLayoutRef.current || !setActive || !mapController?.isReady()) return;
    if (mapController.isLayerActive(slug) && !isActive) {
      setActive(true);
      syncedFromLayoutRef.current = true;
    }
  }, [slug, isActive, setActive, mapController]);

  useEffect(() => {
    if (!mapController || !mapController.isReady() || !isActive) return;
    let cancelled = false;
    setLoading(true);
    const { startDate, endDate } = getDateRange(DEFAULT_DAYS_BACK);
    getBottomTemperature({ startDate, endDate })
      .then((res: { data?: { data?: unknown[] } }) => {
        const temperatureData = res?.data?.data ?? [];
        if (!cancelled) mapController.setBottomTemperatureData(temperatureData);
      })
      .catch((err: unknown) => {
        console.error('Error fetching bottom temperature:', err);
        if (!cancelled) mapController.setBottomTemperatureData([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isActive, mapController, getBottomTemperature]);

  return (
    <div className="control-section">
      <div className="section-title">
        <span className="section-icon">Bottom Temperature</span>
      </div>
      <div
        style={{
          fontSize: '12px',
          color: '#6c757d',
          marginBottom: '18px',
          fontStyle: 'italic',
        }}
      >
        Real-time bottom temperature data from NOAA eMOLT sensors
      </div>
      <div className="form-group">
        <label
          className="checkbox-label"
          style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
        >
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setActive(e.target.checked)
            }
            disabled={!isReady}
            style={{ marginRight: '8px' }}
          />
          <span>Show Temperature Data</span>
          {loading && (
            <span
              style={{ marginLeft: '8px', fontSize: '12px', color: '#6c757d' }}
            >
              (Loading...)
            </span>
          )}
        </label>
      </div>
      {isActive && (
        <div
          style={{
            marginTop: '12px',
            padding: '12px',
            backgroundColor: '#f9f9f9',
            borderRadius: '6px',
            fontSize: '12px',
          }}
        >
          <div
            style={{
              fontWeight: 600,
              marginBottom: '8px',
              color: '#333',
            }}
          >
            Temperature Legend:
          </div>
          {TEMP_LEGEND.map((item, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '6px',
              }}
            >
              <span
                style={{
                  width: '16px',
                  height: '16px',
                  borderRadius: '3px',
                  backgroundColor: item.color,
                  marginRight: '8px',
                  border: '1px solid #ccc',
                }}
              />
              <span style={{ fontSize: '11px', color: '#555' }}>{item.label}</span>
            </div>
          ))}
          <div
            style={{
              marginTop: '12px',
              paddingTop: '8px',
              borderTop: '1px solid #e0e0e0',
              fontSize: '10px',
              color: '#666',
              fontStyle: 'italic',
            }}
          >
            Data: Last {DEFAULT_DAYS_BACK} days from NOAA eMOLT
          </div>
        </div>
      )}
    </div>
  );
}
